import React, { useMemo } from "react"
import { Form } from "antd"
import type { FormInstance } from "antd"
import { useGet } from "utils/useFetch"
import type {
  IDutyLocationDoc,
  IWorkplaceByIdRes,
  IWorkplaceDetailDoc,
} from "../../typings"
import CustomInput from "./CustomInput"
import ShiftTypeCard from "../../components/ShiftTypeCard"
import DateInput, { DateInputValue } from "./DateInput"
import { isBeforeToday } from "../../utils/shiftTablesUtils"

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_NAME_LENGTH = 300

const FORM_LABEL_CLASS =
  "[&_.ant-form-item-label>label]:font-circular [&_.ant-form-item-label>label]:font-medium [&_.ant-form-item-label>label]:text-sm [&_.ant-form-item-label>label]:text-slate-900"

const DEFAULT_DETAILS_VALUES = {
  shiftType: "rostered" as const,
  startDate: { dd: "", mm: "", yyyy: "" } as DateInputValue,
  endDate: { dd: "", mm: "", yyyy: "" } as DateInputValue,
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function toDate(d: DateInputValue): Date | null {
  if (d?.dd && d?.mm && d?.yyyy) return new Date(`${d.yyyy}-${d.mm}-${d.dd}`)
  return null
}

export function toIsoDateString(value?: DateInputValue): string | null {
  const d = value ? toDate(value) : null
  if (!d || Number.isNaN(d.getTime())) return null
  return d.toISOString().slice(0, 10)
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface CreateShiftPlanDetailsProps {
  onShiftTypeChange?: (type: "rostered" | "anytime") => void
  formRef?: React.MutableRefObject<FormInstance | null>
  workplaceId?: string | number
  initialValues?: Partial<{
    shiftPlanName: string
    dutyLocation: string | number
    shiftType: string
    startDate: DateInputValue
    endDate: DateInputValue
  }>
}

// ─── Sub-components ───────────────────────────────────────────────────────────

export function DateInputField({
  value,
  onChange,
}: {
  value?: DateInputValue
  onChange?: (v: DateInputValue) => void
}) {
  return (
    <DateInput
      value={value ?? { dd: "", mm: "", yyyy: "" }}
      onChange={onChange}
    />
  )
}

function ShiftTypeRadioGroup({
  value,
  onChange,
  onShiftTypeChange,
}: {
  value?: "rostered" | "anytime"
  onChange?: (v: "rostered" | "anytime") => void
  onShiftTypeChange?: (type: "rostered" | "anytime") => void
}) {
  const handleSelect = (type: "rostered" | "anytime") => {
    onChange?.(type)
    onShiftTypeChange?.(type)
  }

  return (
    <div className="flex flex-col gap-3">
      <ShiftTypeCard
        title="Rostered (By Day)"
        description="Assign specific shifts to employees on specific days. Best for set schedules."
        expandedDescription="Assign specific shifts to employees on specific days of the week. Each employee has a fixed schedule showing exactly when they work. Perfect for industries where coverage on particular days is critical."
        example="Nurses are scheduled for specific days — Nurse Sarah works Monday, Wednesday, Friday on the morning shift, while Nurse James covers Tuesday and Thursday on the night shift. The roster shows exactly who's working which shift on which day."
        image="https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/eb830b2a-593f-4107-8e9d-fe726a67b210.svg"
        isSelected={value === "rostered"}
        onSelect={() => handleSelect("rostered")}
      />
      <ShiftTypeCard
        title="Flexible (Weekly Quota)"
        description="Employees work up to a weekly quota. Best for results-based work."
        expandedDescription="Employees can clock in on any active day of the week, as long as they meet their minimum weekly shift requirement. You set the quota, they choose when to come in (within the allowed days). Ideal for consulting, project-based work, or flexible arrangements."
        example="A consultant must complete at least 2 shifts per week but can choose which days work best for their schedule. They might work Monday and Thursday one week, then Tuesday and Friday the next — as long as they hit their 2-shift minimum each week."
        image="https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/232d6311-f756-4dab-908b-7c9c20f5c53e.svg"
        isSelected={value === "anytime"}
        onSelect={() => handleSelect("anytime")}
      />
    </div>
  )
}

// ─── Component ────────────────────────────────────────────────────────────────

const CreateShiftPlanDetails: React.FC<CreateShiftPlanDetailsProps> = ({
  onShiftTypeChange,
  formRef,
  workplaceId,
  initialValues: initialValuesProp,
}) => {
  const [form] = Form.useForm()
  const shiftPlanName = Form.useWatch("shiftPlanName", form) ?? ""

  const mergedInitialValues = useMemo(
    () => ({ ...DEFAULT_DETAILS_VALUES, ...initialValuesProp }),
    [initialValuesProp]
  )

  const { data: workplaceRes, isLoading: isWorkplaceLoading } =
    useGet<IWorkplaceByIdRes>({
      url: `/v1/time-attendance/workplaces/${workplaceId}`,
      instanceKey: "ewaAxiosInstance",
      enabled: !!workplaceId,
    })

  const workplace: IWorkplaceDetailDoc | undefined = workplaceRes?.data
  const dutyLocations: IDutyLocationDoc[] = workplace?.officeDutyLocations ?? []

  const dutyLocationOptions = useMemo(() => {
    const workplaceLabel =
      workplace?.displayName ?? workplace?.fullAddress ?? ""
    return [
      ...(workplaceLabel
        ? [{ label: workplaceLabel, value: "workplace" as const }]
        : []),
      ...dutyLocations.map((doc) => ({ label: doc.name, value: doc.id })),
    ]
  }, [workplace, dutyLocations])

  React.useEffect(() => {
    if (formRef) formRef.current = form
  }, [form, formRef])

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={mergedInitialValues}
    >
      <h3 className="font-bold text-lg text-[#0F172B] mb-2">
        Create shift plan
      </h3>

      <CustomInput
        name="shiftPlanName"
        label="Shift plan name"
        control="input"
        placeholder="Give this shift plan a name [300 characters max]"
        className={FORM_LABEL_CLASS}
        inputClassName="pr-4"
        maxLength={MAX_NAME_LENGTH}
        suffix={
          <span className="text-xs text-[#94A3B8] pointer-events-none">
            {shiftPlanName.length} / {MAX_NAME_LENGTH}
          </span>
        }
        rules={[
          { required: true, message: "Please enter a shift plan name" },
          {
            max: MAX_NAME_LENGTH,
            message: `Max ${MAX_NAME_LENGTH} characters`,
          },
        ]}
      />

      <CustomInput
        name="dutyLocation"
        label="Duty location"
        control="select"
        placeholder="Select duty location"
        className={FORM_LABEL_CLASS}
        options={dutyLocationOptions}
        selectLoading={isWorkplaceLoading}
        allowClear
        showSearch
        optionFilterProp="label"
        rules={[{ required: true, message: "Please select a duty location" }]}
      />

      <Form.Item
        label="Start date — End date"
        className={FORM_LABEL_CLASS}
      >
        <div className="flex gap-4 items-end flex-wrap">
          <Form.Item
            name="startDate"
            noStyle
            rules={[
              {
                validator: (_, value: DateInputValue) => {
                  if (!value?.dd || !value?.mm || !value?.yyyy)
                    return Promise.reject("Start date is required")
                  const d = toDate(value)
                  if (d && isNaN(d.getTime()))
                    return Promise.reject("Invalid start date")
                  if (isBeforeToday(value))
                    return Promise.reject("Start date cannot be in the past")
                  return Promise.resolve()
                },
              },
            ]}
          >
            <DateInputField />
          </Form.Item>

          <span className="text-[#94A3B8] pb-2.5">—</span>

          <Form.Item
            name="endDate"
            noStyle
            rules={[
              {
                validator: (_, value: DateInputValue) => {
                  if (!value?.dd || !value?.mm || !value?.yyyy)
                    return Promise.reject("End date is required")
                  const end = toDate(value)
                  if (end && isNaN(end.getTime()))
                    return Promise.reject("Invalid end date")
                  if (isBeforeToday(value))
                    return Promise.reject("End date cannot be in the past")
                  const start = toDate(form.getFieldValue("startDate") ?? {})
                  if (start && end && end < start)
                    return Promise.reject(
                      "End date cannot be before start date"
                    )
                  return Promise.resolve()
                },
              },
            ]}
          >
            <DateInputField />
          </Form.Item>
        </div>
      </Form.Item>

      <Form.Item
        name="shiftType"
        label="Select a shift type"
        className={FORM_LABEL_CLASS}
      >
        <ShiftTypeRadioGroup onShiftTypeChange={onShiftTypeChange} />
      </Form.Item>
    </Form>
  )
}

export default CreateShiftPlanDetails
